import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import Table from 'antd/lib/table';
import Tabs from 'antd/lib/tabs';
import moment from 'moment';
import * as React from 'react';
import {
  ADDITIONAL_CHARGE_DATE_DISPLAY_FORMAT,
  ADDITIONAL_CHARGE_GRID_LABELS,
  ADDITIONAL_CHARGE_GRID_MESSAGES,
  APPLICABILITY_LEVEL_LABELS,
  APPLICABILITY_WISE_LABELS,
  EDITABLE_LINE_STATUSES,
  NON_SUBMITTABLE_LINE_STATUSES,
} from './AdditionalChargeGrids.constants';
import { AdditionalChargeLineDto, AdditionalChargeTabViewModel } from './AdditionalChargeGrids.types';

export interface AdditionalChargeGridsHtmlProps {
  classes: Record<string, string>;
  isLoading: boolean;
  errorMessage?: string;
  isReadOnly: boolean;
  tabs: AdditionalChargeTabViewModel[];
  activeTabKey?: string;
  isSaving: boolean;
  isSubmitting: boolean;
  /** The per-tab exchange-rate footer rows carry their own `pcd` (ISS-292248 F7); the footer no longer
   * needs a task-level PCD prop here. */
  onTabChange: (key: string) => void;
  onRowSelectionChange: (lineIds: string[], selectedKeys: string[]) => void;
  onSaveDraft: () => void;
  onSubmitForApproval: () => void;
  /** Returns to Part 1 to add/remove declared charges. */
  onEditCharges?: () => void;
}

/**
 * Part 2 of the Additional Vendor Charges task — presentational only. Every column, every cell and the
 * date rule are already resolved by the container via the registry; this file only wires the already-
 * built view model into Tabs / Table / buttons. No totals are rendered anywhere on this screen.
 */
export const AdditionalChargeGridsHtml = ({
  classes,
  isLoading,
  errorMessage,
  isReadOnly,
  tabs,
  activeTabKey,
  isSaving,
  isSubmitting,
  onTabChange,
  onRowSelectionChange,
  onSaveDraft,
  onSubmitForApproval,
  onEditCharges,
}: AdditionalChargeGridsHtmlProps) => {
  if (isLoading) {
    return (
      <div className={classes.spinner}>
        <Spin />
      </div>
    );
  }

  if (errorMessage) {
    return <div className={classes.errorMessage}>{errorMessage}</div>;
  }

  if (!tabs.length) {
    return <div className={classes.message}>{ADDITIONAL_CHARGE_GRID_MESSAGES.noChargesDeclared}</div>;
  }

  // Save/Submit apply only when the active charge-type tab still has at least one actionable line
  // (DRAFT / REWORK). If every line in the tab is Approved / Rejected / In Process, hide both.
  const activeTab = tabs.find((tab) => tab.chargeType === activeTabKey) || tabs[0];
  const activeTabHasEditableLine = !!activeTab?.sections.some((section) =>
    section.lines.some((line) => EDITABLE_LINE_STATUSES.indexOf(line.status) >= 0)
  );

  return (
    <div className={classes.container}>
      {/*
        `animated={false}` is load-bearing, not cosmetic: antd 3 animates panes inside a transformed
        track, and a pane that was hidden at mount measures zero width — so switching tabs collapsed
        every section to a sliver and left the panel with a stray horizontal scrollbar.
      */}
      <Tabs
        activeKey={activeTabKey}
        onChange={onTabChange}
        animated={false}
        className={classes.tabs}
      >
        {tabs.map((tab) => (
          <Tabs.TabPane tab={tab.label} key={tab.chargeType}>
            {tab.sections.map((section) => (
              <div className={classes.section} key={`${tab.chargeType}-${section.chargeCode}`}>
                <div className={classes.sectionHeader}>
                  <span className={classes.sectionTitle}>{section.chargeName}</span>
                  <span className={classes.sectionMeta}>
                    {`(${APPLICABILITY_LEVEL_LABELS[section.applicabilityLevel]})`}
                  </span>
                </div>
                <div className={classes.sectionSubtitle}>
                  {APPLICABILITY_WISE_LABELS[section.applicabilityLevel]}
                </div>
                {/*
                  Always the real grid, columns and all — an empty section says so through the table's
                  own `emptyText` ("No BL found" / "No containers found") rather than replacing the
                  columns with prose.
                */}
                <Table
                  rowKey="id"
                  pagination={false}
                  className={classes.table}
                  columns={section.columns}
                  dataSource={section.lines}
                  scroll={{ x: 'max-content' }}
                  locale={section.emptyReason ? { emptyText: section.emptyReason } : undefined}
                  rowSelection={
                    isReadOnly
                      ? undefined
                      : {
                          selectedRowKeys: section.selectedRowKeys,
                          onChange: (selectedKeys) =>
                            onRowSelectionChange(
                              section.lines.map((line: AdditionalChargeLineDto) => line.id),
                              selectedKeys as string[]
                            ),
                          // Approved / rejected lines can't be (re)sent for approval — disable their checkbox.
                          getCheckboxProps: (line: AdditionalChargeLineDto) => ({
                            disabled: NON_SUBMITTABLE_LINE_STATUSES.indexOf(line.status) >= 0,
                          }),
                        }
                  }
                />
              </div>
            ))}

            {/*
              Per tab (ISS-292248): one line per currency THIS tab's lines actually use, e.g.
              "1 USD = 95.496224 INR (as of 20-08-2026)". The payload's rate map is seeded with every
              pickable currency so a freshly-chosen one converts without saving — rendering that map
              directly would list ~20 rates the vendor never touched. A tab using only INR shows nothing.
            */}
            {tab.exchangeRateLines.length > 0 && (
              <div className={classes.exchangeRatesFooter}>
                <strong>{ADDITIONAL_CHARGE_GRID_LABELS.exchangeRatesHeading}</strong>
                {tab.exchangeRateLines.map((entry) => (
                  // Keyed by the whole triple (ISS-292248 F7): the same currency can appear twice at
                  // different per-container PCDs/rates, so the currency alone is not a unique React key.
                  <div key={`${entry.currency}|${entry.rate}|${entry.pcd ?? ''}`}>
                    {`1 ${entry.currency} = ${entry.rate} INR`}
                    {entry.pcd
                      ? ` (as of ${moment(entry.pcd).format(ADDITIONAL_CHARGE_DATE_DISPLAY_FORMAT)})`
                      : ''}
                  </div>
                ))}
              </div>
            )}
          </Tabs.TabPane>
        ))}
      </Tabs>

      {!isReadOnly && (
        <div className={classes.footer}>
          {onEditCharges && (
            <Button className={classes.editChargesBtn} onClick={onEditCharges}>
              {ADDITIONAL_CHARGE_GRID_LABELS.editCharges}
            </Button>
          )}
          {activeTabHasEditableLine && (
            <>
              <Button onClick={onSaveDraft} loading={isSaving}>
                {ADDITIONAL_CHARGE_GRID_LABELS.saveAsDraft}
              </Button>
              <Button type="primary" onClick={onSubmitForApproval} loading={isSubmitting}>
                {ADDITIONAL_CHARGE_GRID_LABELS.sendForApproval}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdditionalChargeGridsHtml;
